import { Box } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import { G, S } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { Cart, CartProduct } from "../../../domain";
import {
  useAvailableAddonsForProduct,
  useAvailableServicesForProduct,
  useCart,
} from "../../../hooks";
import { currencyFormatter } from "../../../util/formatCurrency";
import { ProductImage } from "../../Product";
import * as shopUtils from "../../Shop/utils";
import CustomizationsSummary from "./CustomizationsSummary";
import { cartProductAddons } from "../../CartProductEditor/addons";
import { editableSections } from "../../CartProductEditor/editability";
import { Button } from "../../../themeV2";
import css from "./CartProduct.module.css";
import EditDialog from "./EditDialog";

function hasGiftDetails(cartProduct) {
  return (
    G.isString(cartProduct.recipientEmail) &&
    S.isNotEmpty(cartProduct.recipientEmail)
  );
}

function getItemDiscount(cart, cartProductId) {
  for (const group of cart.shipmentGroups) {
    const item = group.itemized?.[cartProductId];
    if (item) return item.promoCodeDiscount ?? 0;
  }
  return 0;
}

const CartProductElement = React.memo(function CartProductElement({
  cartProduct,
  canEdit = false,
}) {
  const { cart, refresh } = useCart();
  const [isEditing, setIsEditing] = React.useState(false);
  const { availableAddons } = useAvailableAddonsForProduct(
    cartProduct.productId,
  );
  const { services: availableServices } = useAvailableServicesForProduct(
    cartProduct.productId,
  );

  const { addedAddons, unaddedAddons } = React.useMemo(
    () => cartProductAddons({ availableAddons, cartProduct, cart }),
    [cart, cartProduct, availableAddons],
  );

  const sections = React.useMemo(
    () =>
      editableSections({
        cartProduct,
        addedAddons,
        unaddedAddons,
        availableServices,
      }),
    [cartProduct, addedAddons, unaddedAddons, availableServices],
  );

  const handleRemove = React.useCallback(() => {
    Cart.removeProduct(cartProduct.cartProductId)
      .then(() => refresh())
      .catch(console.error);
  }, [cartProduct, refresh]);

  return (
    <Box mb={1}>
      <Box
        display="grid"
        gridTemplateColumns="180px 1fr auto"
        gridTemplateRows="auto"
        alignItems="center"
        width="100%"
        gridGap="16px"
      >
        <ProductImage
          product={cartProduct.toProductLike()}
          isDark={false}
          isSmall
        />

        <div>
          <StyledTitle>{cartProduct.name}</StyledTitle>

          <StyledTypography>
            {shopUtils.signerTextFor(cartProduct)}
          </StyledTypography>

          <StyledTypography>{cartProduct.productType}</StyledTypography>

          {Boolean(cartProduct.personalizationTo) && (
            <StyledTypography>
              <strong>To: </strong>
              {cartProduct.personalizationTo}
            </StyledTypography>
          )}

          <StyledTypography>
            <strong>Special instructions: </strong>
            {cartProduct.notes || "None"}
          </StyledTypography>

          {hasGiftDetails(cartProduct) && (
            <StyledTypography>
              <strong>Recipient Email: </strong>
              <span>{cartProduct.recipientEmail}</span>
            </StyledTypography>
          )}
        </div>

        <Box textAlign="right">
          <StyledPrice>{cartProduct.subtotal.formattedAmount}</StyledPrice>
          {(() => {
            const discount = cart.promoCode?.productTagString
              ? getItemDiscount(cart, cartProduct.cartProductId)
              : 0;

            return discount < 0 ? (
              <StyledPromoDiscount>
                {currencyFormatter.format(discount / 100)} promo
              </StyledPromoDiscount>
            ) : null;
          })()}
        </Box>
      </Box>

      <CustomizationsSummary
        addons={addedAddons}
        cartProductId={cartProduct.cartProductId}
        services={cartProduct.services}
      />

      <Box
        mt={0.5}
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-start"
      >
        {canEdit && (
          <div className={css.actions}>
            {sections.hasAnything && (
              <Button
                variant="text"
                size="sm"
                onClick={() => setIsEditing(true)}
                aria-label={`Edit product ${cartProduct.name}`}
              >
                Edit product
              </Button>
            )}

            <Button
              variant="text"
              size="sm"
              onClick={handleRemove}
              aria-label={`Remove ${cartProduct.name}`}
            >
              Remove
            </Button>
          </div>
        )}
      </Box>

      {isEditing && (
        <EditDialog
          cartProduct={cartProduct}
          onClose={() => setIsEditing(false)}
        />
      )}
    </Box>
  );
});

CartProductElement.propTypes = {
  cartProduct: PropTypes.instanceOf(CartProduct),
  canEdit: PropTypes.bool,
};

const StyledTitle = styled("h4")(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: 600,
  color: theme.palette.text.primary,
  margin: 0,
  marginTop: theme.spacing(0.5),
}));

const StyledTypography = styled("p")(({ theme }) => ({
  ...theme.typography.caption,
  margin: 0,
  lineHeight: 1.35,
  "& strong": {
    color: theme.palette.text.secondary,
  },
}));

const StyledPrice = styled("span")(({ theme }) => ({
  ...theme.typography.body1,
  fontWeight: 500,
}));

const StyledPromoDiscount = styled("p")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.green?.main ?? theme.palette.success.main,
  margin: 0,
}));

export default CartProductElement;
